import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  Avatar,
  Box,
  Chip,
  CircularProgress,
  IconButton,
  Paper,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import {
  AutoAwesome,
  Close,
  DeleteSweepOutlined,
  OpenInFull,
  SendRounded,
  ZoomOutMapRounded,
} from "@mui/icons-material";
import ReactMarkdown from "react-markdown";
import axiosInstance from "../axiosInstance";

const STARTER_PROMPTS = [
  "Where can I find the tenant ID?",
  "What is the product index name?",
  "What data feed type does this tenant use?",
];

const PAGE_LABELS = {
  tenants: "Tenant Management",
  importer: "Importer Status",
  products: "Products",
  agents: "Agents",
};

const INITIAL_MESSAGE = {
  id: "assistant-welcome",
  role: "assistant",
  content:
    "Hi! I can help you find your way around this page and explain documented settings. What would you like to know?",
};

const VIEWPORT_MARGIN = 16;
const DEFAULT_PANEL_WIDTH = 390;
const DEFAULT_PANEL_HEIGHT = 560;
const MIN_PANEL_WIDTH = 300;
const MIN_PANEL_HEIGHT = 320;
const PANEL_SIZE_PRESETS = [
  { name: "compact", label: "Compact", width: 390, height: 560 },
  { name: "medium", label: "Medium", width: 560, height: 680 },
  { name: "large", label: "Large", width: 760, height: 800 },
  { name: "focus", label: "Focus", width: Infinity, height: Infinity },
];
const LAUNCHER_FADE_DELAY = 4000;
const WAITING_MESSAGES = [
  "Working on your request. Please wait…",
  "Working through your question…",
  "Putting together a response…",
  "Thanks for your patience. Still working…",
  "Taking a closer look at your question…",
  "Considering the details…",
  "Preparing an answer for you…",
  "Working on a helpful response…",
  "Still on it. Thanks for waiting…",
  "Please bear with me while I work on this…",
];
const WAITING_MESSAGE_INTERVAL = 2000;
const HEX_COLOR_PATTERN =
  /#[0-9a-fA-F]{8}(?![0-9a-fA-F])|#[0-9a-fA-F]{6}(?![0-9a-fA-F])|#[0-9a-fA-F]{4}(?![0-9a-fA-F])|#[0-9a-fA-F]{3}(?![0-9a-fA-F])/g;

const addColorSwatches = () => (tree) => {
  const visit = (node) => {
    if (!Array.isArray(node.children)) return;

    node.children = node.children.flatMap((child) => {
      if (child.type !== "text") {
        visit(child);
        return child;
      }

      const parts = [];
      let lastIndex = 0;

      for (const match of child.value.matchAll(HEX_COLOR_PATTERN)) {
        if (match.index > lastIndex) {
          parts.push({
            type: "text",
            value: child.value.slice(lastIndex, match.index),
          });
        }

        parts.push({
          type: "element",
          tagName: "span",
          properties: { "data-color": match[0] },
          children: [{ type: "text", value: match[0] }],
        });
        lastIndex = match.index + match[0].length;
      }

      if (lastIndex === 0) return child;
      if (lastIndex < child.value.length) {
        parts.push({ type: "text", value: child.value.slice(lastIndex) });
      }
      return parts;
    });
  };

  visit(tree);
};

const ColorValue = ({ children, ...props }) => {
  const color = props["data-color"];

  return (
    <Box
      component="span"
      sx={{
        display: "inline-flex",
        alignItems: "center",
        gap: 0.5,
        whiteSpace: "nowrap",
      }}
    >
      <Box
        component="span"
        role="img"
        aria-label={`Color ${color}`}
        title={color}
        sx={{
          width: "0.95em",
          height: "0.95em",
          flexShrink: 0,
          borderRadius: "3px",
          backgroundColor: color,
          border: "1px solid rgba(17, 24, 39, 0.22)",
          boxShadow: "inset 0 0 0 1px rgba(255, 255, 255, 0.35)",
        }}
      />
      {children}
    </Box>
  );
};

const AssistantMessageContent = ({ content }) => (
  <Box
    sx={{
      fontSize: "0.88rem",
      lineHeight: 1.55,
      textAlign: "left",
      overflowWrap: "anywhere",
      "& > :first-of-type": { mt: 0 },
      "& > :last-child": { mb: 0 },
      "& p": { my: 0.8 },
      "& h1, & h2, & h3, & h4, & h5, & h6": {
        mt: 1.25,
        mb: 0.6,
        fontSize: "1em",
        lineHeight: 1.35,
        fontWeight: 800,
      },
      "& ul, & ol": { my: 0.8, pl: 2.5 },
      "& li": { mb: 0.35 },
      "& blockquote": {
        my: 0.8,
        mx: 0,
        pl: 1.25,
        color: "#596273",
        borderLeft: "3px solid rgba(249, 115, 22, 0.38)",
      },
      "& code": {
        px: 0.45,
        py: 0.15,
        borderRadius: "4px",
        backgroundColor: "#f4f5f7",
        fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
        fontSize: "0.84em",
      },
      "& pre": {
        my: 0.8,
        p: 1,
        overflowX: "auto",
        borderRadius: "8px",
        backgroundColor: "#f4f5f7",
      },
      "& pre code": { p: 0, backgroundColor: "transparent" },
      "& a": { color: "#b8510f", fontWeight: 600 },
      "& img": {
        display: "block",
        width: "180px",
        maxWidth: "60%",
        height: "auto",
        marginInline: "auto",
      },
    }}
  >
    <ReactMarkdown
      rehypePlugins={[addColorSwatches]}
      components={{
        a: ({ children, href, title }) => (
          <a href={href} title={title} target="_blank" rel="noreferrer">
            {children}
          </a>
        ),
        span: ({ children, ...props }) =>
          props["data-color"] ? (
            <ColorValue {...props}>{children}</ColorValue>
          ) : (
            <span>{children}</span>
          ),
      }}
    >
      {content}
    </ReactMarkdown>
  </Box>
);

const constrainGeometry = (geometry) => {
  const maxWidth = Math.max(0, window.innerWidth - VIEWPORT_MARGIN * 2);
  const maxHeight = Math.max(0, window.innerHeight - VIEWPORT_MARGIN * 2);
  const minWidth = Math.min(MIN_PANEL_WIDTH, maxWidth);
  const minHeight = Math.min(MIN_PANEL_HEIGHT, maxHeight);
  const width = Math.min(Math.max(geometry.width, minWidth), maxWidth);
  const height = Math.min(Math.max(geometry.height, minHeight), maxHeight);
  const maxX = Math.max(
    VIEWPORT_MARGIN,
    window.innerWidth - width - VIEWPORT_MARGIN
  );
  const maxY = Math.max(
    VIEWPORT_MARGIN,
    window.innerHeight - height - VIEWPORT_MARGIN
  );

  return {
    width,
    height,
    x: Math.min(Math.max(geometry.x, VIEWPORT_MARGIN), maxX),
    y: Math.min(Math.max(geometry.y, VIEWPORT_MARGIN), maxY),
  };
};

const getInitialGeometry = () => {
  const width = Math.min(
    DEFAULT_PANEL_WIDTH,
    window.innerWidth - VIEWPORT_MARGIN * 2
  );
  const height = Math.min(
    DEFAULT_PANEL_HEIGHT,
    window.innerHeight - VIEWPORT_MARGIN * 2
  );

  return constrainGeometry({
    width,
    height,
    x: window.innerWidth - width - 24,
    y: window.innerHeight - height - 164,
  });
};

const getPresetGeometry = (presetIndex, currentGeometry) => {
  const preset = PANEL_SIZE_PRESETS[presetIndex] || PANEL_SIZE_PRESETS[0];
  const maxWidth = Math.max(0, window.innerWidth - VIEWPORT_MARGIN * 2);
  const maxHeight = Math.max(0, window.innerHeight - VIEWPORT_MARGIN * 2);
  const width = Math.min(preset.width, maxWidth);
  const height = Math.min(preset.height, maxHeight);
  const rightOffset = Math.max(
    presetIndex === 0 ? 24 : VIEWPORT_MARGIN,
    window.innerWidth - currentGeometry.x - currentGeometry.width
  );
  const bottomOffset = Math.max(
    presetIndex === 0 ? 164 : VIEWPORT_MARGIN,
    window.innerHeight - currentGeometry.y - currentGeometry.height
  );

  return constrainGeometry({
    width,
    height,
    x: window.innerWidth - width - rightOffset,
    y: window.innerHeight - height - bottomOffset,
  });
};

const normaliseQuestion = (value) =>
  value.toLowerCase().replace(/[^\w\s]/g, " ").replace(/\s+/g, " ").trim();

const generateSessionId = () => {
  if (window.crypto && typeof window.crypto.randomUUID === "function") {
    return window.crypto.randomUUID();
  }

  return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (character) => {
    const randomValue = (Math.random() * 16) | 0;
    const value =
      character === "x" ? randomValue : (randomValue & 0x3) | 0x8;
    return value.toString(16);
  });
};

const buildAssistantPayload = ({
  sessionId,
  messages,
  question,
  context,
}) => ({
  SessionId: sessionId,
  ChatHistory: messages
    .filter((message) => message.id !== INITIAL_MESSAGE.id)
    .map(
      (message) =>
        `${message.role === "user" ? "User" : "Assistant"}: ${message.content}`
    ),
  Question: question,
  Context: {
    Page: context?.Page || "tenants",
    TenantId: context?.TenantId || null,
    areaId: context?.areaId || null,
    SectionId: context?.SectionId || null,
    SubsectionId: context?.SubsectionId ?? null,
  },
});

const extractAssistantReply = (responseData) => {
  if (typeof responseData === "string") return responseData;
  if (!responseData || typeof responseData !== "object") return "";

  const reply =
    responseData.Answer ??
    responseData.answer ??
    responseData.Response ??
    responseData.response ??
    responseData.Message ??
    responseData.message ??
    responseData.Content ??
    responseData.content;

  if (typeof reply === "string") return reply;
  if (responseData.data !== undefined) {
    return extractAssistantReply(responseData.data);
  }
  return "";
};

const submitAssistantPayload = async (payload) => {
  const response = await axiosInstance.post("/Assistant/chat", payload);
  return extractAssistantReply(response.data);
};

const buildAssistantReply = (question, tenantName) => {
  const normalised = normaliseQuestion(question);
  const tenantContext = tenantName ? ` for ${tenantName}` : "";

  if (normalised.includes("tenant id") || normalised.includes("tenant identifier")) {
    return "Open Tenant Management, select the tenant, then open Overview. The tenant ID also appears as a read-only value in Advanced > Metadata.";
  }

  if (
    normalised.includes("save") ||
    normalised.includes("publish") ||
    normalised.includes("changes")
  ) {
    return "Most configuration sections use the page-level Save Changes action. Enrichment Settings, Order Tracking, Jobs, Caches, Assets, and Integration use their own section-specific actions. I’m read-only, so I can explain the steps but can’t save or publish changes.";
  }

  if (normalised.includes("draft")) {
    return `When a configuration draft exists${tenantContext}, Tenant Management displays it in place of the published tenant details. Check the draft and publishing state before assuming the values shown are live.`;
  }

  if (
    normalised.includes("permission") ||
    normalised.includes("access") ||
    normalised.includes("cannot see") ||
    normalised.includes("can't see") ||
    normalised.includes("section")
  ) {
    return "Assets are available to everyone except for the Preezie root tenant, where they are admin-only. The original Cache section is admin-only; Cache V2 and Integration are full-admin-only.";
  }

  return "I don’t have a documented answer for that yet. Try asking where a Tenant Management setting lives, how changes are saved, why draft values appear, or which sections require additional access.";
};

const createMessage = (role, content) => ({
  id: `${role}-${Date.now()}-${Math.random().toString(36).slice(2)}`,
  role,
  content,
});

const AssistantChat = ({
  tenantName = "",
  context = {},
  onSubmitPayload,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [draft, setDraft] = useState("");
  const [messages, setMessages] = useState([INITIAL_MESSAGE]);
  const [geometry, setGeometry] = useState(getInitialGeometry);
  const [sizePresetIndex, setSizePresetIndex] = useState(0);
  const [isGeometryInteracting, setIsGeometryInteracting] = useState(false);
  const [isLauncherIdle, setIsLauncherIdle] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const [waitingMessageIndex, setWaitingMessageIndex] = useState(0);
  const sessionIdRef = useRef(null);
  const inputRef = useRef(null);
  const messageListRef = useRef(null);
  const interactionRef = useRef(null);
  const launcherFadeTimerRef = useRef(null);

  if (!sessionIdRef.current) {
    sessionIdRef.current = generateSessionId();
  }

  const hasConversation = messages.some((message) => message.role === "user");
  const pageLabel = PAGE_LABELS[context?.Page] || PAGE_LABELS.tenants;
  const contextualLabel = useMemo(
    () =>
      tenantName && context?.Page !== "agents"
        ? `${pageLabel} · ${tenantName}`
        : pageLabel,
    [context?.Page, pageLabel, tenantName]
  );

  const keepLauncherVisible = useCallback(() => {
    window.clearTimeout(launcherFadeTimerRef.current);
    setIsLauncherIdle(false);
  }, []);

  const scheduleLauncherFade = useCallback(() => {
    keepLauncherVisible();
    if (!isOpen) {
      launcherFadeTimerRef.current = window.setTimeout(
        () => setIsLauncherIdle(true),
        LAUNCHER_FADE_DELAY
      );
    }
  }, [isOpen, keepLauncherVisible]);

  useEffect(() => {
    if (!isOpen) return undefined;

    inputRef.current?.focus();
    const handleEscape = (event) => {
      if (event.key === "Escape") setIsOpen(false);
    };

    window.addEventListener("keydown", handleEscape);
    return () => window.removeEventListener("keydown", handleEscape);
  }, [isOpen]);

  useEffect(() => {
    scheduleLauncherFade();
    return () => window.clearTimeout(launcherFadeTimerRef.current);
  }, [scheduleLauncherFade]);

  useEffect(() => {
    if (!isSending) return undefined;

    const interval = window.setInterval(() => {
      setWaitingMessageIndex((current) => (
        current + 1 + Math.floor(Math.random() * (WAITING_MESSAGES.length - 1))
      ) % WAITING_MESSAGES.length);
    }, WAITING_MESSAGE_INTERVAL);

    return () => window.clearInterval(interval);
  }, [isSending]);

  useEffect(() => {
    if (!isOpen) return;
    const list = messageListRef.current;
    if (list) list.scrollTop = list.scrollHeight;
  }, [isOpen, messages, isSending]);

  useEffect(() => {
    const keepPanelInViewport = () => {
      setGeometry((current) => constrainGeometry(current));
    };

    window.addEventListener("resize", keepPanelInViewport);
    return () => window.removeEventListener("resize", keepPanelInViewport);
  }, []);

  const startInteraction = useCallback(
    (event, type) => {
      if (event.button !== 0) return;
      if (type === "drag" && event.target.closest("button")) return;

      event.preventDefault();
      interactionRef.current = {
        type,
        pointerId: event.pointerId,
        startX: event.clientX,
        startY: event.clientY,
        geometry,
      };
      setIsGeometryInteracting(true);
      event.currentTarget.setPointerCapture?.(event.pointerId);
    },
    [geometry]
  );

  const updateInteraction = useCallback((event) => {
    const interaction = interactionRef.current;
    if (!interaction || interaction.pointerId !== event.pointerId) return;

    const deltaX = event.clientX - interaction.startX;
    const deltaY = event.clientY - interaction.startY;

    setGeometry(
      constrainGeometry(
        interaction.type === "drag"
          ? {
              ...interaction.geometry,
              x: interaction.geometry.x + deltaX,
              y: interaction.geometry.y + deltaY,
            }
          : {
              ...interaction.geometry,
              width: interaction.geometry.width + deltaX,
              height: interaction.geometry.height + deltaY,
            }
      )
    );
  }, []);

  const endInteraction = useCallback((event) => {
    if (interactionRef.current?.pointerId === event.pointerId) {
      interactionRef.current = null;
      setIsGeometryInteracting(false);
      event.currentTarget.releasePointerCapture?.(event.pointerId);
    }
  }, []);

  const resizeWithKeyboard = (event) => {
    const step = event.shiftKey ? 40 : 20;
    const sizeChange = {
      ArrowLeft: { width: -step, height: 0 },
      ArrowRight: { width: step, height: 0 },
      ArrowUp: { width: 0, height: -step },
      ArrowDown: { width: 0, height: step },
    }[event.key];

    if (!sizeChange) return;
    event.preventDefault();
    setGeometry((current) =>
      constrainGeometry({
        ...current,
        width: current.width + sizeChange.width,
        height: current.height + sizeChange.height,
      })
    );
  };

  const cyclePanelSize = () => {
    const nextIndex = (sizePresetIndex + 1) % PANEL_SIZE_PRESETS.length;
    setGeometry((current) => getPresetGeometry(nextIndex, current));
    setSizePresetIndex(nextIndex);
  };

  const currentSizePreset = PANEL_SIZE_PRESETS[sizePresetIndex];
  const nextSizePreset =
    PANEL_SIZE_PRESETS[(sizePresetIndex + 1) % PANEL_SIZE_PRESETS.length];

  const sendMessage = async (content = draft) => {
    const question = content.trim();
    if (!question || isSending) return;

    const payload = buildAssistantPayload({
      sessionId: sessionIdRef.current,
      messages,
      question,
      context,
    });
    const userMessage = createMessage("user", question);

    setMessages((current) => [...current, userMessage]);
    setDraft("");
    setWaitingMessageIndex(Math.floor(Math.random() * WAITING_MESSAGES.length));
    setIsSending(true);

    try {
      const submittedReply = onSubmitPayload
        ? await onSubmitPayload(payload)
        : await submitAssistantPayload(payload);
      const assistantReply =
        typeof submittedReply === "string" && submittedReply.trim()
          ? submittedReply
          : buildAssistantReply(question, tenantName);

      setMessages((current) => [
        ...current,
        createMessage("assistant", assistantReply),
      ]);
    } catch {
      setMessages((current) => [
        ...current,
        createMessage(
          "assistant",
          "I couldn’t complete that request. Please try again."
        ),
      ]);
    } finally {
      setIsSending(false);
    }
  };

  const resetConversation = () => {
    setMessages([INITIAL_MESSAGE]);
    setDraft("");
    sessionIdRef.current = generateSessionId();
    inputRef.current?.focus();
  };

  return (
    <>
      <Box
        sx={{
          position: "fixed",
          right: { xs: 16, sm: 24 },
          bottom: { xs: 86, sm: 94 },
          zIndex: 1360,
        }}
      >
        <Tooltip
          title={isOpen ? "Close AI assistant" : "Open AI assistant"}
          placement="left"
        >
          <IconButton
            aria-label={isOpen ? "Close AI assistant" : "Open AI assistant"}
            aria-expanded={isOpen}
            aria-controls={isOpen ? "cms-assistant-panel" : undefined}
            onMouseEnter={keepLauncherVisible}
            onMouseLeave={scheduleLauncherFade}
            onFocus={keepLauncherVisible}
            onBlur={scheduleLauncherFade}
            onClick={() => {
              keepLauncherVisible();
              setIsOpen((open) => !open);
            }}
            sx={{
              width: 58,
              height: 58,
              opacity: isLauncherIdle ? 0.16 : 1,
              color: "#fff",
              background: "linear-gradient(135deg, #ff8a3d, #f56a13)",
              border: "1px solid rgba(255, 255, 255, 0.55)",
              boxShadow: "0 12px 28px rgba(239, 108, 28, 0.34)",
              transition:
                "opacity 0.35s ease, transform 0.2s ease, box-shadow 0.2s ease",
              "&:hover": {
                color: "#fff",
                background: "linear-gradient(135deg, #ff934d, #ef6210)",
                transform: "translateY(-2px)",
                boxShadow: "0 16px 34px rgba(239, 108, 28, 0.4)",
              },
              "&:focus-visible": {
                outline: "3px solid rgba(249, 115, 22, 0.28)",
                outlineOffset: 3,
              },
            }}
          >
            {isOpen ? (
              <Close />
            ) : (
              <AutoAwesome />
            )}
          </IconButton>
        </Tooltip>
      </Box>

      {isOpen && (
        <Paper
          id="cms-assistant-panel"
          role="region"
          aria-label="AI assistant chat"
          elevation={0}
          sx={{
            position: "fixed",
            zIndex: 1350,
            left: geometry.x,
            top: geometry.y,
            width: geometry.width,
            height: geometry.height,
            display: "flex",
            flexDirection: "column",
            overflow: "hidden",
            borderRadius: currentSizePreset.name === "focus" ? "18px" : "22px",
            border: "1px solid rgba(255, 132, 76, 0.18)",
            backgroundColor: "rgba(255, 255, 255, 0.98)",
            backdropFilter: "blur(20px)",
            boxShadow:
              currentSizePreset.name === "focus"
                ? "0 32px 100px rgba(67, 43, 28, 0.28)"
                : "0 28px 80px rgba(67, 43, 28, 0.24)",
            transition: (theme) =>
              isGeometryInteracting
                ? "none"
                : theme.transitions.create(
                    [
                      "left",
                      "top",
                      "width",
                      "height",
                      "border-radius",
                      "box-shadow",
                    ],
                    {
                      duration: 360,
                      easing: theme.transitions.easing.easeInOut,
                    }
                  ),
            "@media (prefers-reduced-motion: reduce)": {
              transition: "none",
            },
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1.25,
              px: 1.75,
              py: 1.4,
              borderBottom: "1px solid rgba(255, 132, 76, 0.12)",
              background:
                "linear-gradient(135deg, rgba(255, 244, 237, 0.98), rgba(255, 255, 255, 0.98))",
              cursor: "move",
              touchAction: "none",
              userSelect: "none",
            }}
            data-testid="assistant-drag-handle"
            aria-label="Move AI assistant"
            onPointerDown={(event) => startInteraction(event, "drag")}
            onPointerMove={updateInteraction}
            onPointerUp={endInteraction}
            onPointerCancel={endInteraction}
          >
            <Avatar
              sx={{
                width: 38,
                height: 38,
                color: "#fff",
                background: "linear-gradient(135deg, #ff8a3d, #f56a13)",
                boxShadow: "0 8px 18px rgba(239, 108, 28, 0.22)",
              }}
            >
              <AutoAwesome sx={{ fontSize: 21 }} />
            </Avatar>

            <Box sx={{ minWidth: 0, flex: 1, textAlign: "left" }}>
              <Typography
                component="h2"
                sx={{ color: "#202531", fontSize: "1rem", fontWeight: 800 }}
              >
                AI assistant
              </Typography>
              <Typography
                variant="caption"
                noWrap
                sx={{ display: "block", color: "#6b7280" }}
              >
                {contextualLabel}
              </Typography>
            </Box>

            <Tooltip
              title={
                currentSizePreset.name === "focus"
                  ? "Return to compact size"
                  : `Expand to ${nextSizePreset.label.toLowerCase()} size`
              }
            >
              <IconButton
                aria-label={
                  currentSizePreset.name === "focus"
                    ? "Return AI assistant to compact size"
                    : `Expand AI assistant to ${nextSizePreset.label.toLowerCase()} size`
                }
                aria-live="polite"
                onClick={cyclePanelSize}
                size="small"
                sx={{
                  position: "relative",
                  width: 34,
                  height: 34,
                  color: "#6b7280",
                  border: "1px solid rgba(249, 115, 22, 0.12)",
                  backgroundColor: "rgba(255, 255, 255, 0.7)",
                  boxShadow: "0 4px 12px rgba(89, 52, 28, 0.06)",
                  transition:
                    "color 180ms ease, background-color 180ms ease, border-color 180ms ease, transform 180ms ease",
                  "&:hover": {
                    color: "#f97316",
                    backgroundColor: "#fff",
                    borderColor: "rgba(249, 115, 22, 0.3)",
                    transform: "translateY(-1px)",
                  },
                  "&:focus-visible": {
                    outline: "2px solid rgba(249, 115, 22, 0.32)",
                    outlineOffset: 2,
                  },
                }}
              >
                <ZoomOutMapRounded sx={{ fontSize: 18 }} />
                <Box
                  aria-hidden="true"
                  sx={{
                    position: "absolute",
                    left: "50%",
                    bottom: 3,
                    display: "flex",
                    gap: "2px",
                    transform: "translateX(-50%)",
                  }}
                >
                  {PANEL_SIZE_PRESETS.map((preset, index) => (
                    <Box
                      key={preset.name}
                      component="span"
                      sx={{
                        width: index === sizePresetIndex ? 5 : 3,
                        height: 2,
                        borderRadius: 2,
                        backgroundColor:
                          index === sizePresetIndex ? "#f97316" : "#cbd0d7",
                        transition: "width 180ms ease, background-color 180ms ease",
                      }}
                    />
                  ))}
                </Box>
              </IconButton>
            </Tooltip>

            <Tooltip title="Start a new chat">
              <span>
                <IconButton
                  aria-label="Start a new chat"
                  onClick={resetConversation}
                  disabled={!hasConversation || isSending}
                  size="small"
                  sx={{
                    color: "#6b7280",
                    "&:hover": {
                      color: "#ef4444",
                      backgroundColor: "rgba(239, 68, 68, 0.08)",
                    },
                  }}
                >
                  <DeleteSweepOutlined fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
            <IconButton
              aria-label="Close AI assistant"
              onClick={() => setIsOpen(false)}
              size="small"
              sx={{
                color: "#6b7280",
                "&:hover": {
                  color: "#f97316",
                  backgroundColor: "rgba(249, 115, 22, 0.08)",
                },
              }}
            >
              <Close fontSize="small" />
            </IconButton>
          </Box>

          <Box
            ref={messageListRef}
            role="log"
            aria-live="polite"
            aria-label="Conversation"
            sx={{
              flex: 1,
              minHeight: 0,
              overflowY: "auto",
              px: 1.75,
              py: 2,
              background:
                "radial-gradient(circle at top right, rgba(255, 237, 226, 0.7), transparent 34%), #fffdfc",
            }}
          >
            {messages.map((message) => (
              <Box
                key={message.id}
                sx={{
                  display: "flex",
                  justifyContent:
                    message.role === "user" ? "flex-end" : "flex-start",
                  mb: 1.4,
                }}
              >
                <Box
                  sx={{
                    minWidth: 0,
                    maxWidth: sizePresetIndex >= 2 ? "min(72%, 680px)" : "86%",
                    px: 1.5,
                    py: 1.15,
                    borderRadius:
                      message.role === "user"
                        ? "16px 16px 4px 16px"
                        : "16px 16px 16px 4px",
                    color: message.role === "user" ? "#fff" : "#303744",
                    background:
                      message.role === "user"
                        ? "linear-gradient(135deg, #ff8a3d, #f56a13)"
                        : "#fff",
                    border:
                      message.role === "user"
                        ? "none"
                        : "1px solid rgba(17, 24, 39, 0.08)",
                    boxShadow:
                      message.role === "user"
                        ? "0 8px 18px rgba(239, 108, 28, 0.18)"
                        : "0 6px 18px rgba(17, 24, 39, 0.06)",
                  }}
                >
                  {message.role === "assistant" ? (
                    <AssistantMessageContent content={message.content} />
                  ) : (
                    <Typography
                      sx={{
                        fontSize: "0.88rem",
                        lineHeight: 1.55,
                        textAlign: "left",
                        whiteSpace: "pre-wrap",
                      }}
                    >
                      {message.content}
                    </Typography>
                  )}
                </Box>
              </Box>
            ))}

            {isSending && (
              <Box
                role="status"
                aria-live="polite"
                aria-atomic="true"
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                  width: "fit-content",
                  maxWidth: "86%",
                  px: 1.5,
                  py: 1.15,
                  mb: 1.4,
                  borderRadius: "16px 16px 16px 4px",
                  backgroundColor: "#fff",
                  border: "1px solid rgba(17, 24, 39, 0.08)",
                  boxShadow: "0 6px 18px rgba(17, 24, 39, 0.06)",
                  color: "#6b7280",
                }}
              >
                <CircularProgress
                  size={16}
                  aria-hidden="true"
                  sx={{ color: "#f97316", flexShrink: 0 }}
                />
                <Typography sx={{ fontSize: "0.88rem", lineHeight: 1.55 }}>
                  {WAITING_MESSAGES[waitingMessageIndex]}
                </Typography>
              </Box>
            )}

            {!hasConversation && (
              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75, mt: 0.5 }}>
                {STARTER_PROMPTS.map((prompt) => (
                  <Chip
                    key={prompt}
                    label={prompt}
                    onClick={() => sendMessage(prompt)}
                    disabled={isSending}
                    variant="outlined"
                    sx={{
                      height: "auto",
                      borderColor: "rgba(249, 115, 22, 0.24)",
                      color: "#9a4d16",
                      backgroundColor: "rgba(255, 247, 241, 0.8)",
                      "& .MuiChip-label": {
                        display: "block",
                        py: 0.7,
                        whiteSpace: "normal",
                        textAlign: "left",
                        fontSize: "0.78rem",
                      },
                      "&:hover": {
                        borderColor: "rgba(249, 115, 22, 0.45)",
                        backgroundColor: "#fff1e8",
                      },
                    }}
                  />
                ))}
              </Box>
            )}
          </Box>

          <Box
            component="form"
            onSubmit={(event) => {
              event.preventDefault();
              sendMessage();
            }}
            sx={{
              display: "flex",
              alignItems: "flex-end",
              gap: 1,
              p: 1.4,
              pr: 5.5,
              borderTop: "1px solid rgba(17, 24, 39, 0.07)",
              backgroundColor: "#fff",
            }}
          >
            <TextField
              inputRef={inputRef}
              value={draft}
              disabled={isSending}
              onChange={(event) => setDraft(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === "Enter" && !event.shiftKey) {
                  event.preventDefault();
                  sendMessage();
                }
              }}
              placeholder="Ask about Tenant Management…"
              multiline
              maxRows={4}
              fullWidth
              size="small"
              inputProps={{ "aria-label": "Message AI assistant" }}
              sx={{
                "& .MuiOutlinedInput-root": {
                  borderRadius: "15px",
                  backgroundColor: "#fffaf7",
                  fontSize: "0.9rem",
                  "& fieldset": { borderColor: "rgba(17, 24, 39, 0.10)" },
                  "&:hover fieldset": { borderColor: "rgba(249, 115, 22, 0.35)" },
                  "&.Mui-focused fieldset": {
                    borderColor: "#f97316",
                    borderWidth: 1,
                  },
                },
              }}
            />
            <IconButton
              type="submit"
              aria-label="Send message"
              disabled={!draft.trim() || isSending}
              sx={{
                width: 42,
                height: 42,
                flexShrink: 0,
                color: "#fff",
                background: "linear-gradient(135deg, #ff8a3d, #f56a13)",
                boxShadow: "0 8px 18px rgba(239, 108, 28, 0.22)",
                "&:hover": {
                  background: "linear-gradient(135deg, #ff934d, #ef6210)",
                },
                "&.Mui-disabled": {
                  color: "#b8bec7",
                  background: "#eef0f3",
                  boxShadow: "none",
                },
              }}
            >
              {isSending ? (
                <CircularProgress size={18} color="inherit" />
              ) : (
                <SendRounded fontSize="small" />
              )}
            </IconButton>
          </Box>

          <IconButton
            aria-label="Resize AI assistant"
            title="Drag to resize AI assistant"
            onPointerDown={(event) => startInteraction(event, "resize")}
            onPointerMove={updateInteraction}
            onPointerUp={endInteraction}
            onPointerCancel={endInteraction}
            onKeyDown={resizeWithKeyboard}
            size="small"
            sx={{
              position: "absolute",
              right: 2,
              bottom: 2,
              width: 32,
              height: 32,
              color: "#b4bac4",
              cursor: "nwse-resize",
              touchAction: "none",
              borderRadius: "9px",
              "&:hover": {
                color: "#f97316",
                backgroundColor: "rgba(249, 115, 22, 0.08)",
              },
            }}
          >
            <OpenInFull sx={{ fontSize: 17, transform: "rotate(90deg)" }} />
          </IconButton>
        </Paper>
      )}
    </>
  );
};

export {
  addColorSwatches,
  ColorValue,
  PANEL_SIZE_PRESETS,
  buildAssistantPayload,
  buildAssistantReply,
  extractAssistantReply,
  generateSessionId,
  getPresetGeometry,
  submitAssistantPayload,
};
export default AssistantChat;
